<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import ThreeSixtyDialogWhatsapp from './360DialogWhatsapp.vue';
import CloudWhatsapp from './CloudWhatsapp.vue';
import WhatsappEmbeddedSignup from './WhatsappEmbeddedSignup.vue';
import ChannelSelector from 'dashboard/components/ChannelSelector.vue';
import BaileysWhatsapp from './BaileysWhatsapp.vue';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const PROVIDER_TYPES = {
  WHATSAPP: 'whatsapp',
  WHATSAPP_CLOUD: 'whatsapp_cloud',
  WHATSAPP_EMBEDDED: 'whatsapp_embedded',
  WHATSAPP_MANUAL: 'whatsapp_manual',
  THREE_SIXTY_DIALOG: '360dialog',
  BAILEYS: 'baileys',
};

const hasWhatsappAppId = computed(() => {
  return (
    window.chatauto360Config?.whatsappAppId &&
    window.chatauto360Config.whatsappAppId !== 'none'
  );
});

const selectedProvider = computed(() => route.query.provider);

const showProviderSelection = computed(() => !selectedProvider.value);

const showConfiguration = computed(() => Boolean(selectedProvider.value));

const secondaryProviders = computed(() => [
  {
    key: PROVIDER_TYPES.BAILEYS,
    title: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.BAILEYS'),
    description: t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.BAILEYS_DESC'),
    icon: 'i-woot-baileys',
  },
]);

const selectProvider = providerValue => {
  router.push({
    name: route.name,
    params: route.params,
    query: { provider: providerValue },
  });
};

const shouldShowCloudWhatsapp = provider => {
  return (
    provider === PROVIDER_TYPES.WHATSAPP_MANUAL ||
    (provider === PROVIDER_TYPES.WHATSAPP && !hasWhatsappAppId.value)
  );
};

const handleManualLinkClick = () => {
  selectProvider(PROVIDER_TYPES.WHATSAPP_MANUAL);
};
</script>

<template>
  <div class="overflow-auto col-span-6 p-6 w-full h-full">
    <div v-if="showProviderSelection">
      <div class="mb-8 text-left">
        <h1 class="mb-2 text-lg font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.TITLE') }}
        </h1>
        <p class="text-sm leading-relaxed text-n-slate-11 max-w-2xl">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.DESCRIPTION') }}
        </p>
      </div>

      <button
        type="button"
        class="relative w-full max-w-3xl text-left p-6 rounded-2xl border border-n-brand/40 bg-n-brand/5 hover:bg-n-brand/10 hover:border-n-brand transition-colors cursor-pointer group"
        @click="selectProvider(PROVIDER_TYPES.WHATSAPP)"
      >
        <span
          class="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-n-brand text-white text-xs font-medium"
        >
          <span class="i-lucide-sparkles size-3.5" />
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.RECOMMENDED_BADGE') }}
        </span>

        <div class="flex items-start gap-4">
          <div
            class="flex-shrink-0 size-14 rounded-xl bg-n-brand/15 flex items-center justify-center"
          >
            <span class="i-woot-whatsapp size-8 text-n-brand" />
          </div>
          <div class="flex-1 min-w-0">
            <h2 class="text-base font-semibold text-n-slate-12 mb-1">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD') }}
            </h2>
            <p class="text-sm text-n-slate-11 leading-relaxed">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.PROVIDERS.WHATSAPP_CLOUD_DESC') }}
            </p>
          </div>
          <span
            class="i-lucide-arrow-right size-5 text-n-slate-10 group-hover:text-n-brand group-hover:translate-x-0.5 transition-all"
          />
        </div>
      </button>

      <div class="mt-10">
        <div class="mb-4">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.ADVANCED_OPTIONS_LABEL'
              )
            }}
          </h3>
          <p class="text-xs text-n-slate-11 mt-0.5">
            {{
              $t(
                'INBOX_MGMT.ADD.WHATSAPP.SELECT_PROVIDER.ADVANCED_OPTIONS_DESCRIPTION'
              )
            }}
          </p>
        </div>

        <div class="flex gap-4 justify-start flex-wrap">
          <ChannelSelector
            v-for="provider in secondaryProviders"
            :key="provider.key"
            :title="provider.title"
            :description="provider.description"
            :icon="provider.icon"
            @click="selectProvider(provider.key)"
          />
        </div>
      </div>
    </div>

    <div v-else-if="showConfiguration">
      <div class="px-6 py-5 rounded-2xl border border-n-weak">
        <div
          v-if="
            hasWhatsappAppId && selectedProvider === PROVIDER_TYPES.WHATSAPP
          "
        >
          <WhatsappEmbeddedSignup />

          <div class="pt-6 mt-6 border-t border-n-weak">
            <I18nT
              keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_FALLBACK"
              tag="p"
              class="text-sm text-n-slate-11"
            >
              <template #link>
                <a
                  href="#"
                  class="underline text-n-brand"
                  @click.prevent="handleManualLinkClick"
                >
                  {{
                    $t(
                      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.MANUAL_LINK_TEXT'
                    )
                  }}
                </a>
              </template>
            </I18nT>
          </div>
        </div>

        <CloudWhatsapp v-else-if="shouldShowCloudWhatsapp(selectedProvider)" />

        <ThreeSixtyDialogWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.THREE_SIXTY_DIALOG"
        />
        <CloudWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.WHATSAPP"
        />
        <BaileysWhatsapp
          v-else-if="selectedProvider === PROVIDER_TYPES.BAILEYS"
        />
      </div>
    </div>
  </div>
</template>
